import axios from "axios";

// Client API del Chatbot. LF non ha un interceptor axios globale: il token JWT
// da localStorage viene allegato a mano in ogni chiamata (pattern RegistrazioniCall.js).

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
const BASE = `${API_URL}/chat`;

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

export const chatApi = {
    // provider abilitati + modelli, per il selettore
    listModels: () =>
        axios.get(`${BASE}/models`, { headers: authHeader() }).then((r) => r.data),

    // conversazioni dell'utente (sidebar)
    listConversations: () =>
        axios.get(`${BASE}/conversations`, { headers: authHeader() }).then((r) => r.data),

    // conversazione singola + messaggi
    getConversation: (id) =>
        axios.get(`${BASE}/conversations/${id}`, { headers: authHeader() }).then((r) => r.data),

    // crea conversazione vuota col provider scelto. `model` è facoltativo: se manca o è
    // stato dismesso, il server ricade sul più recente disponibile (task 452).
    createConversation: (provider_type, model) =>
        axios.post(`${BASE}/conversations`, { provider_type, model }, { headers: authHeader() }).then((r) => r.data),

    // invia un messaggio e ricevi la risposta dell'LLM (non-streaming)
    sendMessage: (id, content) =>
        axios.post(`${BASE}/conversations/${id}/messages`, { content }, { headers: authHeader() }).then((r) => r.data),

    // rinomina
    renameConversation: (id, title) =>
        axios.patch(`${BASE}/conversations/${id}`, { title }, { headers: authHeader() }).then((r) => r.data),

    // cambia il modello LLM di una conversazione avviata (es. se quello salvato è deprecato)
    updateModel: (id, model) =>
        axios.patch(`${BASE}/conversations/${id}/model`, { model }, { headers: authHeader() }).then((r) => r.data),

    // interrompe la generazione in corso sulla conversazione (tasto Stop, task 452).
    // Il server abortisce la chiamata al provider e salva solo il parziale già mostrato.
    stopGeneration: (id) =>
        axios.post(`${BASE}/conversations/${id}/stop`, {}, { headers: authHeader() }).then((r) => r.data),

    // cambia versione attiva del turno (freccette) -> ritorna il nuovo percorso attivo
    activateMessage: (id, messageId) =>
        axios
            .post(`${BASE}/conversations/${id}/messages/${messageId}/activate`, {}, { headers: authHeader() })
            .then((r) => r.data),

    // elimina
    deleteConversation: (id) =>
        axios.delete(`${BASE}/conversations/${id}`, { headers: authHeader() }).then((r) => r.data),

    // --- Allegati ---
    // Carica uno o più file (immagini/documenti) sulla conversazione. Vanno collegati
    // al messaggio successivo passando gli id in attachmentIds all'invio.
    // Risponde in STREAMING: il server manda l'avanzamento mentre legge i documenti
    // (l'OCR di una scansione dura minuti). Niente axios qui, servono gli eventi man
    // mano. `onProgress` riceve { file, fase: 'caricamento'|'analisi'|'ocr', pagina?, totale? }.
    uploadAttachments: async (id, files, onProgress) => {
        const fd = new FormData();
        for (const f of files) fd.append("files", f);
        // NB: niente Content-Type manuale: lo imposta il browser, col boundary giusto.
        const response = await fetch(`${BASE}/conversations/${id}/attachments`, {
            method: "POST",
            headers: authHeader(),
            body: fd,
        });
        let attachments = [];
        for await (const ev of readSSE(response)) {
            if (ev.type === "progress") onProgress?.(ev);
            else if (ev.type === "done") attachments = ev.attachments || [];
            else if (ev.type === "error") throw new Error(ev.error);
            // 'ping': battito per il proxy, non significa niente per noi
        }
        return { attachments };
    },

    // --- Preferiti (per-utente) ---
    addFavorite: (id) =>
        axios.post(`${BASE}/conversations/${id}/favorite`, {}, { headers: authHeader() }).then((r) => r.data),

    removeFavorite: (id) =>
        axios.delete(`${BASE}/conversations/${id}/favorite`, { headers: authHeader() }).then((r) => r.data),

    // --- Membri (condivisione) ---
    listMembers: (id) =>
        axios.get(`${BASE}/conversations/${id}/members`, { headers: authHeader() }).then((r) => r.data),

    listInvitableUsers: (id) =>
        axios.get(`${BASE}/conversations/${id}/invitable-users`, { headers: authHeader() }).then((r) => r.data),

    inviteMember: (id, userId) =>
        axios.post(`${BASE}/conversations/${id}/members`, { userId }, { headers: authHeader() }).then((r) => r.data),

    removeMember: (id, userId) =>
        axios.delete(`${BASE}/conversations/${id}/members/${userId}`, { headers: authHeader() }).then((r) => r.data),

    // --- Config provider (solo admin/superadmin) ---
    listProviders: () =>
        axios.get(`${BASE}/providers`, { headers: authHeader() }).then((r) => r.data),

    // payload: { enabled?, api_key? } — api_key viene cifrata lato server
    updateProvider: (providerType, payload) =>
        axios.put(`${BASE}/providers/${providerType}`, payload, { headers: authHeader() }).then((r) => r.data),

    // --- Materiali grafici / brand (solo admin/superadmin, task 453) ---
    listBrands: () => axios.get(`${BASE}/brands`, { headers: authHeader() }).then((r) => r.data),

    // payload: { nome?, descrizione?, colore_primario?, colore_secondario?, colore_accento?,
    //            font?, footer_text?, is_default? (solo true: il predefinito si sposta, non si toglie) }
    updateBrand: (key, payload) =>
        axios.put(`${BASE}/brands/${key}`, payload, { headers: authHeader() }).then((r) => r.data),

    // Un file per volta. Risposta JSON secca (niente OCR, quindi niente SSE come per gli
    // allegati): basta axios, che imposta da solo il Content-Type multipart.
    uploadBrandAsset: (key, file, kind, descrizione) => {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", kind);
        fd.append("descrizione", descrizione || "");
        return axios.post(`${BASE}/brands/${key}/assets`, fd, { headers: authHeader() }).then((r) => r.data);
    },

    deleteBrandAsset: (key, assetId) =>
        axios.delete(`${BASE}/brands/${key}/assets/${assetId}`, { headers: authHeader() }).then((r) => r.data),
};

// Legge una risposta SSE e emette gli eventi { type: 'token'|'done'|'error', ... }.
// Usa fetch + ReadableStream (axios non gestisce bene lo streaming nel browser).
async function* readSSE(response) {
    // Errore PRIMA dello stream (es. 400/404): il corpo e' JSON.
    if (!response.ok) {
        let err = "Errore durante l'operazione.";
        try {
            err = (await response.json()).error || err;
        } catch {
            /* corpo non-JSON */
        }
        throw new Error(err);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            // gli eventi SSE sono separati da una riga vuota (\n\n)
            const chunks = buffer.split("\n\n");
            buffer = chunks.pop() || "";
            for (const chunk of chunks) {
                const dataLine = chunk.split("\n").find((l) => l.startsWith("data: "));
                if (!dataLine) continue;
                try {
                    yield JSON.parse(dataLine.slice(6));
                } catch {
                    /* riga malformata: skip */
                }
            }
        }
    } finally {
        reader.releaseLock();
    }
}

// Invio (o modifica, se editMessageId) di un messaggio con risposta in streaming.
// attachmentIds: id degli allegati caricati da collegare a questo messaggio utente.
export async function* streamMessage(id, content, signal, editMessageId, attachmentIds) {
    const token = localStorage.getItem("token");
    const body = { content };
    // editMessageId presente = modifica: crea una nuova versione del messaggio e rigenera.
    if (editMessageId) body.editMessageId = editMessageId;
    if (attachmentIds && attachmentIds.length) body.attachmentIds = attachmentIds;
    const response = await fetch(`${BASE}/conversations/${id}/messages/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
        signal, // permette lo Stop: abort() interrompe la fetch (throw AbortError)
    });
    yield* readSSE(response);
}

// Rigenera la risposta: nuova versione (fratello) dell'assistente, in streaming.
export async function* streamRegenerate(id, messageId, signal) {
    const token = localStorage.getItem("token");
    const response = await fetch(`${BASE}/conversations/${id}/messages/${messageId}/regenerate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        signal,
    });
    yield* readSSE(response);
}
